import { cn } from "../../lib/cn";
import type { TaskStatus, TrackStatus } from "../../lib/types";

export type AnyStatus = TaskStatus | TrackStatus;

/**
 * Maestro status → semantic CSS variable.
 * Single source of truth so every component renders the same color per status.
 */
export const STATUS_VAR: Record<AnyStatus, string> = {
  backlog: "var(--status-queued)",
  todo: "var(--status-queued)",
  planning: "var(--status-queued)",
  ready: "var(--status-queued)",
  in_progress: "var(--status-running)",
  verifying: "var(--status-running)",
  needs_input: "var(--status-paused)",
  in_review: "var(--status-review)",
  done: "var(--status-success)",
  interrupted: "var(--status-error)",
};

const LABEL: Record<AnyStatus, string> = {
  backlog: "Backlog",
  todo: "Todo",
  planning: "Planning",
  ready: "Ready",
  in_progress: "In progress",
  verifying: "Verifying",
  needs_input: "Needs input",
  in_review: "In review",
  done: "Done",
  interrupted: "Interrupted",
};

interface Props {
  status: AnyStatus;
  size?: number;
  pulse?: boolean;
  title?: string;
  className?: string;
}

export function StatusDot({ status, size = 8, pulse, title, className }: Props) {
  const color = STATUS_VAR[status];
  return (
    <span
      role="status"
      aria-label={title || LABEL[status]}
      title={title || LABEL[status]}
      className={cn("relative inline-flex shrink-0", className)}
      style={{ width: size, height: size }}
    >
      {pulse && (
        <span
          className="absolute inset-0 rounded-full motion-safe:animate-ping"
          style={{ background: color, opacity: 0.45 }}
        />
      )}
      <span
        className="relative inline-block rounded-full"
        style={{ width: size, height: size, background: color }}
      />
    </span>
  );
}

export function statusLabel(status: AnyStatus): string {
  return LABEL[status];
}

/**
 * Color for the per-status left rail on cards/tiles/rows. Neutral statuses
 * (backlog / todo / planning / ready) fall back to `--rail-neutral` so an
 * idle card still gets a deliberate edge without the rail shouting; everything
 * else reuses the semantic STATUS_VAR. Single source of truth so TaskCard,
 * the DragOverlay, and the Stage tiles all agree.
 */
export function statusRailColor(status: AnyStatus): string {
  return status === "backlog" ||
    status === "todo" ||
    status === "planning" ||
    status === "ready"
    ? "var(--rail-neutral)"
    : STATUS_VAR[status];
}
